import { isAfter } from 'date-fns/isAfter';
import { subHours } from 'date-fns/subHours';
import i18n from 'src/i18n';
import { isCanceled, isGuest } from 'src/models/visit/utils';

import type { Appointment } from 'src/models/appointment/_types';
import type { User } from 'src/models/user/_types';

export function getAppointmentType(appointment: Appointment): string {
  const { t } = i18n;
  const recurs = isAppointmentRecurring(appointment);
  if (appointment.isGroupClass) {
    return `${recurs ? t('admin.platform.appointment.recurring') + ' ' : ''}${t(
      'admin.platform.appointment.class'
    )}`;
  }

  return `${recurs ? t('admin.platform.appointment.recurring') + ' ' : ''}Appointment`;
}

interface Staff {
  user: User;
  isPrimary: boolean;
}

export function isSpotAvailable(appointment: Appointment): boolean {
  if (appointment.isGroupClass) {
    return (
      appointment.visits.filter((v) => !isCanceled(v)).length <
      appointment.maxVisits
    );
  }

  return appointment.visits.length < 1;
}

export function getAppointmentStaff(appointment: Appointment): Staff[] {
  let staff: Staff[] = [];

  staff = appointment.primaryTrainers.map((user) => ({
    user,
    isPrimary: true,
  }));

  staff = staff.concat(
    appointment.secondaryTrainers.map((user) => ({
      user,
      isPrimary: false,
    }))
  );

  return staff;
}

export function isAppointmentCancelLate({ appointment, session }): boolean {
  const servicePolicy = appointment.service.cancellationPolicyHours;
  const platformPolicy = session.cancellationPolicyHours;
  let policy = 24;

  if (servicePolicy || servicePolicy == 0) {
    policy = servicePolicy;
  } else if (platformPolicy || platformPolicy == 0) {
    policy = platformPolicy;
  }

  // is now *after* the appointment start time minus the policy
  return isAfter(new Date(), subHours(appointment.startTime, policy));
}

export function isRescheduleDisabledByLateCancellationPolicy({
  appointment,
  session,
}): boolean {
  // If the setting is not enabled, allow rescheduling
  if (!session?.disableLateCancellationRescheduling) {
    return false;
  }

  // If the setting is enabled, check if we're within the late cancellation window
  return isAppointmentCancelLate({ appointment, session });
}

export function isAppointmentRecurring(appointment: Appointment): boolean {
  return !!appointment.scheduledAppointmentId;
}

export function isAppointmentFuture(appointment: Appointment): boolean {
  return new Date(appointment.startTime) > new Date();
}

export function guestCount(appointment: Appointment): number {
  return appointment.visits.filter((v) => isGuest(v)).length;
}
